import { useMemo } from 'react';

import { formatUnits } from 'viem';
import { useAccount } from 'wagmi';
import { useReadContract } from 'wagmi';

import { predictionContractAbi } from '@/abi';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

import type { Address } from 'viem';

interface I_UsePendingRefundsProps {
  participation?: T_UserBlockchainData['predictionEventParticipations'][0];
}

/**
 * Hook to read pending refunds from the contract for a specific participation
 * Returns the refund amount in decimal format and whether refund is available
 */
export function usePendingRefunds({ participation }: I_UsePendingRefundsProps) {
  const { address } = useAccount();
  const contractAddress =
    participation?.predictionEvent?.predictionContract?.contractAddress;

  const isCanceled = participation?.predictionEvent?.canceled === true;

  // Read pending refunds from contract
  const { data: pendingRefunds, refetch: refetchPendingRefunds } =
    useReadContract({
      address: contractAddress as Address | undefined,
      abi: predictionContractAbi,
      functionName: 'pendingRefunds',
      args: address ? [address] : undefined,
      query: {
        enabled: !!contractAddress && !!address && !!isCanceled
      }
    }) as { data: bigint | undefined; refetch: () => Promise<unknown> };

  // Convert to decimal string
  const refundAmountDecimal = useMemo(() => {
    if (!pendingRefunds) return '0';
    return formatUnits(pendingRefunds, C_USDC_DECIMALS);
  }, [pendingRefunds]);

  // Check if refund is available (pendingRefunds > 0)
  const hasPendingRefund = useMemo(() => {
    if (!pendingRefunds) return false;
    return pendingRefunds > BigInt(0);
  }, [pendingRefunds]);

  return {
    refundAmountDecimal,
    hasPendingRefund,
    refetchPendingRefunds
  };
}
